import { useCallback, useEffect, useState } from "react";
import { isFactorySampleId } from "../../../../src/lib/audio";
import { listSampleAssets, subscribeSampleCatalog } from "../lib/sampleLibrary";

export type ImportedSampleOption = {
  id: string;
  name: string;
};

/**
 * Host sample library entries (non-factory) for Sampler sample pickers.
 * Call refresh() after imports or when opening the dropdown.
 */
export function useImportedSampleCatalog(): {
  imported: ImportedSampleOption[];
  refresh: () => void;
} {
  const [imported, setImported] = useState<ImportedSampleOption[]>([]);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setTick(t => t + 1);
  }, []);

  useEffect(() => subscribeSampleCatalog(refresh), [refresh]);

  useEffect(() => {
    let cancelled = false;
    void listSampleAssets().then(assets => {
      if (cancelled) {
        return;
      }
      const next = assets
        .filter(a => !isFactorySampleId(a.id) && a.source !== "factory")
        .map(a => ({ id: a.id, name: a.name }))
        .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
      setImported(next);
    });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { imported, refresh };
}

/** Keep a currently selected imported id visible even if missing from the index. */
export function withCurrentImportedSample(
  imported: ImportedSampleOption[],
  currentId: string | undefined,
  fallbackName: string,
): ImportedSampleOption[] {
  if (!currentId || isFactorySampleId(currentId)) {
    return imported;
  }
  if (imported.some(s => s.id === currentId)) {
    return imported;
  }
  const label = fallbackName?.trim()
    ? `${fallbackName} (missing)`
    : "Missing sample";
  return [...imported, { id: currentId, name: label }]
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}
